import { JourneyInterface, QuestInterface } from "@swivl-mono/types";
import { SelectItem, SelectOption, SelectableOption } from "@swivl-mono/ui";
import { useEffect, useMemo } from "react";

import { QuestsModel, WorkflowsJourney } from "../Models/Quests/Quests.Model";

type QuestOptionsFormat = "select" | "dropdown";

function getSelectQuestOptions(
  workflows: WorkflowsJourney[],
  prependOptions?: SelectOption[],
  filterJourney?: (journey: JourneyInterface) => boolean,
  filterQuest?: (quest: QuestInterface) => boolean
): SelectItem[] {
  const questOptions: SelectItem[] = [];

  for (const workflow of workflows) {
    if (workflow.quests.length === 0) continue;
    if (filterJourney && !filterJourney(workflow.journey!)) continue;

    const journeyQuests: SelectOption[] = [];

    for (const quest of workflow.quests) {
      if (filterQuest && !filterQuest(quest)) continue;

      journeyQuests.push({ value: quest.slug, label: quest.name });
    }

    if (journeyQuests.length > 0) {
      questOptions.push({
        type: "group",
        label: workflow.journey?.name || "Unassigned",
        options: journeyQuests
      });
    }
  }

  return prependOptions ? [...prependOptions, ...questOptions] : questOptions;
}

function getDropdownQuestOptions(
  workflows: WorkflowsJourney[],
  prependOptions?: SelectableOption[],
  filterJourney?: (journey: JourneyInterface) => boolean,
  filterQuest?: (quest: QuestInterface) => boolean
): SelectableOption[] {
  const questOptions: SelectableOption[] = [];

  for (const workflow of workflows) {
    if (workflow.quests.length === 0) continue;
    if (filterJourney && !filterJourney(workflow.journey!)) continue;

    const journeyQuests: SelectableOption[] = [];

    for (const quest of workflow.quests) {
      if (filterQuest && !filterQuest(quest)) continue;

      journeyQuests.push({ value: quest.slug, label: quest.name });
    }

    if (journeyQuests.length > 0) {
      questOptions.push({ isHeading: true, label: workflow.journey?.name || "Unassigned" });
      questOptions.push(...journeyQuests);
    }
  }

  return prependOptions ? [...prependOptions, ...questOptions] : questOptions;
}

// overloads for different formats
export function useQuestOptions(
  prependOptions?: SelectOption[],
  filterJourney?: (journey: JourneyInterface) => boolean,
  filterQuest?: (quest: QuestInterface) => boolean
): SelectItem[] | undefined;

export function useQuestOptions(
  prependOptions: SelectableOption[] | undefined,
  filterJourney: ((journey: JourneyInterface) => boolean) | undefined,
  filterQuest: ((quest: QuestInterface) => boolean) | undefined,
  format: "dropdown"
): SelectableOption[] | undefined;

export function useQuestOptions(
  prependOptions?: SelectOption[] | SelectableOption[],
  filterJourney?: (journey: JourneyInterface) => boolean,
  filterQuest?: (quest: QuestInterface) => boolean,
  format: QuestOptionsFormat = "select"
): SelectItem[] | SelectableOption[] | undefined {
  const { workflows } = QuestsModel.useState((s) => s);

  useEffect(() => {
    QuestsModel.loadQuestsList(false);
  }, []);

  const options = useMemo<SelectItem[] | SelectableOption[] | undefined>(() => {
    if (!workflows) {
      return undefined;
    }

    if (format === "dropdown") {
      return getDropdownQuestOptions(workflows, prependOptions as SelectableOption[] | undefined, filterJourney, filterQuest);
    }

    return getSelectQuestOptions(workflows, prependOptions as SelectOption[] | undefined, filterJourney, filterQuest);
  }, [workflows, prependOptions, filterJourney, filterQuest, format]);

  return options;
}
